import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Drawer,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { apiErrorMessage } from '../lib/apiError';
import { EmailLink, PhoneLink } from './ContactLinks';
import {
  blankValues,
  FormFields,
  validateFields,
  type Field,
  type FieldErrors,
  type FormValues,
} from './FormFields';

export {
  addressFields,
  EMAIL_FIELD,
  optional,
  optionalNumber,
  PHONE_FIELD,
  required,
  type Field,
  type FormValues,
} from './FormFields';
import { titleCase } from '../lib/format';
import { TablePager, useTablePage } from './TablePager';

export interface Column<Row> {
  label: string;
  render: (row: Row) => ReactNode;
}

export function emailColumn<Row extends { email: string | null }>(): Column<Row> {
  return {
    label: 'Email',
    render: (row) => <EmailLink email={row.email} />,
  };
}

export function phoneColumn<Row extends { phone: string | null }>(): Column<Row> {
  return {
    label: 'Phone',
    render: (row) => <PhoneLink phone={row.phone} />,
  };
}

export interface ListResult<Row> {
  rows: Row[];
  total: number;
  isPending: boolean;
  isError: boolean;
}

export interface ListParams {
  q: string;
  page: number;
  pageSize: number;
}

interface Props<Row extends { id: number }> {
  title: string;
  singular: string;
  fields: Field[];
  columns: Column<Row>[];
  canManage: boolean;
  useRows: (params: ListParams) => ListResult<Row>;
  toValues: (row: Row) => FormValues;
  create: (values: FormValues) => Promise<unknown>;
  update: (id: number, values: FormValues) => Promise<unknown>;
  remove: (id: number) => Promise<unknown>;
  hideSearch?: boolean;
  /** One line on what this list is for. */
  description?: string;
  /** The form when editing, if it asks less than the form for adding. */
  editFields?: Field[];
  /** Totals shown above the list. */
  summary?: ReactNode;
  /** Adds a "Files & notes" button that opens this panel for a row. */
  renderExtras?: (row: Row) => ReactNode;
}

export function RecordPage<Row extends { id: number }>(props: Props<Row>) {
  const { title, singular, fields, columns, canManage, useRows, toValues } = props;
  const [q, setQ] = useState('');
  const paging = useTablePage();
  const { page, pageSize, setPage } = paging;
  // A quick action elsewhere can link here with ?add=1 to open the form straight away.
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState<{ id: number | null; values: FormValues } | null>(() =>
    canManage && searchParams.has('add') ? { id: null, values: blankValues(fields) } : null,
  );
  const [deleting, setDeleting] = useState<Row | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [extrasFor, setExtrasFor] = useState<Row | null>(null);
  const queryClient = useQueryClient();
  const list = useRows({ q, page: page + 1, pageSize });
  const formFields = editing?.id === null ? fields : (props.editFields ?? fields);

  useEffect(() => {
    if (searchParams.has('add')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  async function run(action: () => Promise<unknown>, onDone: () => void) {
    setSaving(true);
    setError(null);
    try {
      await action();
      await queryClient.invalidateQueries();
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function save(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    const { id, values } = editing;
    const found = validateFields(formFields, values);
    setFieldErrors(found);
    if (Object.keys(found).length > 0) return;
    void run(
      () => (id === null ? props.create(values) : props.update(id, values)),
      () => setEditing(null),
    );
  }

  function setValue(name: string, value: string | boolean) {
    setEditing((current) =>
      current ? { ...current, values: { ...current.values, [name]: value } } : current,
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">{title}</Typography>
          {props.description && <Typography color="text.secondary">{props.description}</Typography>}
        </Box>
        {!props.hideSearch && (
          <TextField
            size="small"
            placeholder="Search"
            slotProps={{ htmlInput: { 'aria-label': `Search ${title}` } }}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
          />
        )}
        {canManage && (
          <Button
            variant="contained"
            onClick={() => {
              setError(null);
              setFieldErrors({});
              setEditing({ id: null, values: blankValues(fields) });
            }}
          >
            Add {singular}
          </Button>
        )}
      </Stack>

      {props.summary}

      <Paper variant="outlined">
        {list.isError && <Alert severity="error">Couldn't load this list. Please try again.</Alert>}
        {list.isPending ? (
          <Box sx={{ display: 'grid', placeItems: 'center', p: 4 }}>
            <CircularProgress aria-label="Loading" />
          </Box>
        ) : list.rows.length === 0 && !list.isError ? (
          <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            {q
              ? `Nothing matches "${q}".`
              : `No ${title.toLowerCase()} yet.${canManage ? ` Use "Add ${singular}" to add the first one.` : ''}`}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c.label}>{c.label}</TableCell>
                  ))}
                  {(canManage || props.renderExtras) && (
                    <TableCell align="right">Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {list.rows.map((row) => (
                  <TableRow key={row.id} hover>
                    {columns.map((c) => (
                      <TableCell key={c.label}>{c.render(row)}</TableCell>
                    ))}
                    {(canManage || props.renderExtras) && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {props.renderExtras && (
                          <Button size="small" onClick={() => setExtrasFor(row)}>
                            Files &amp; notes
                          </Button>
                        )}
                        {canManage && (
                          <>
                            <Button
                              size="small"
                              onClick={() => {
                                setError(null);
                                setFieldErrors({});
                                setEditing({ id: row.id, values: toValues(row) });
                              }}
                            >
                              Edit
                            </Button>
                            <Tooltip title={`Remove this ${singular}`}>
                              <IconButton
                                size="small"
                                aria-label={`Remove this ${singular}`}
                                onClick={() => {
                                  setError(null);
                                  setDeleting(row);
                                }}
                              >
                                ×
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePager paging={paging} total={list.total} />
      </Paper>

      <Drawer anchor="right" open={extrasFor !== null} onClose={() => setExtrasFor(null)}>
        <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 3 }}>
          <Stack direction="row" sx={{ alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ flexGrow: 1 }}>
              Files &amp; Notes
            </Typography>
            <Button onClick={() => setExtrasFor(null)}>Close</Button>
          </Stack>
          {extrasFor && props.renderExtras?.(extrasFor)}
        </Box>
      </Drawer>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={save}>
          <DialogTitle>
            {titleCase(editing?.id === null ? `Add ${singular}` : `Edit ${singular}`)}
          </DialogTitle>
          <DialogContent>
            {error && <Alert severity="error">{error}</Alert>}
            {editing && (
              <FormFields
                fields={formFields}
                values={editing.values}
                errors={fieldErrors}
                onChange={setValue}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>{titleCase(`Remove this ${singular}?`)}</DialogTitle>
        <DialogContent>
          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <Typography>It will no longer appear in your lists.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button
            color="error"
            disabled={saving}
            onClick={() => {
              if (deleting)
                void run(
                  () => props.remove(deleting.id),
                  () => setDeleting(null),
                );
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
